import { HttpErrorResponse, HttpResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { routes } from '@tig-dpqa-cloud/contract-backend-backoffice';
import { findTrackedContractRoute, getTrackedContractRoutes } from '../request-activity-route-matcher';
import { HttpErrorStatusBarService } from '../services/http-error-status-bar.service';
import { LoadingQueueService } from '../services/loading-queue.service';
import { SKIP_REQUEST_ACTIVITY } from './request-activity.context';

const trackedRoutes = getTrackedContractRoutes(routes);
const trackedErroredRequestsByService = new WeakMap<HttpErrorStatusBarService, Set<string>>();

let requestCounter = 0;

/**
 * Check if a request (path + method) matches any of the tracked contract routes.
 */
const isTrackedRoute = (url: string, method: string): boolean => {
  try {
    return !!findTrackedContractRoute(url, method, trackedRoutes);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[RequestActivityInterceptor] Error checking if route is tracked:', error);
    return false;
  }
};

const generateRequestId = (req: HttpRequest<unknown>): string => {
  requestCounter += 1;
  return `${Date.now()}-${requestCounter}-${req.method}-${req.urlWithParams}`;
};

const getTrackedRequestKey = (req: HttpRequest<unknown>): string => `${req.method}:${req.url}`;

const getTrackedErroredRequests = (httpErrorStatusBarService: HttpErrorStatusBarService): Set<string> => {
  const existing = trackedErroredRequestsByService.get(httpErrorStatusBarService);
  if (existing) {
    return existing;
  }

  const created = new Set<string>();
  trackedErroredRequestsByService.set(httpErrorStatusBarService, created);
  return created;
};

export const shouldTrackRequest = (req: HttpRequest<unknown>): boolean => {
  if (req.context.get(SKIP_REQUEST_ACTIVITY)) {
    return false;
  }

  return isTrackedRoute(req.url, req.method);
};

export const shouldReportTrackedRequestError = (error: unknown): boolean => {
  if (!(error instanceof HttpErrorResponse)) {
    return true;
  }

  if (error.status === 0 && error.error?.name === 'AbortError') {
    return false;
  }

  return error.status === 0 || error.status >= 500;
};

/** Tracks loading for contract routes and reports generic server/offline errors via HttpErrorStatusBarService. */
export const requestActivityInterceptor: HttpInterceptorFn = (req, next) => {
  try {
    if (!shouldTrackRequest(req)) {
      return next(req);
    }

    const loadingQueue = inject(LoadingQueueService);
    const httpErrorStatusBarService = inject(HttpErrorStatusBarService);
    const trackedErroredRequests = getTrackedErroredRequests(httpErrorStatusBarService);
    const requestId = generateRequestId(req);
    const trackedRequestKey = getTrackedRequestKey(req);
    loadingQueue.start(requestId, req);

    return next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && trackedErroredRequests.has(trackedRequestKey)) {
          trackedErroredRequests.delete(trackedRequestKey);
          httpErrorStatusBarService.clear();
        }
      }),
      catchError((error) => {
        if (shouldReportTrackedRequestError(error)) {
          trackedErroredRequests.add(trackedRequestKey);
          httpErrorStatusBarService.reportGenericError();
        }

        return throwError(() => error);
      }),
      finalize(() => {
        loadingQueue.end(requestId);
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[RequestActivityInterceptor] Unexpected error:', error);
    return next(req);
  }
};
